import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { setLeadData } from 'leadData/leadSlice';

import { StepsContent } from './stepsContent';

import { ContractFlowClient } from 'v3/api/client/ContractFlowClient';

import { useQuery } from 'v3/hooks/useQuery';

import { isProduction } from 'v3/utils/checkEnv';
import { handleErrorMessage } from 'v3/utils/errorHandler';
import LoadingState from 'v3/components/LoadingState';
import FlowScreen from 'v3/components/FlowScreen';

import * as S from './styles';

/* Where a visit lands when it cannot start the flow. The simulator is a useful
   thing to arrive at without a deal in hand, and unlike /deals it is a route of
   this app, so it is a client-side navigation rather than a page load. */
const NO_FLOW_REDIRECT = '/simulator';

/* A club that does not exist is a broken link rather than a bare visit, so it still
   goes out to the public deals page. */
const UNKNOWN_CLUB_REDIRECT = isProduction()
  ? 'https://contractflow.savings.club/deals/'
  : '/deals';

const Flow = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const clubId = query.get('clubId') ?? '';
  const cfUuid = query.get('cfUuid');
  const dealValue = Number(query.get('dealValue'));
  const seats = Number(query.get('seats')) || 1;

  const [isContractFlowLoading, setIsContractFlowLoading] = useState(true);
  const { currentStep } = useSelector((state: RootState) => state.lead);

  const dispatch = useDispatch();
  const { leadData } = useSelector((state: RootState) => state.lead);

  useEffect(() => {
    if ((!clubId && !cfUuid) || (!dealValue && clubId)) {
      navigate(NO_FLOW_REDIRECT, { replace: true });
    }

    // moving the code from this useEffect to the one that also loads the group data
    // if (dealValue && clubId) {
    //   dispatch(
    //     setLeadData({
    //       leadData: {
    //         ...leadData,
    //         dealValue,
    //         clubId,
    //         contractFlowToken: cfUuid,
    //       },
    //     })
    //   );
    // }
  }, [cfUuid, clubId, dealValue, leadData, navigate]);
  const loadingGroupDataRef = useRef(false);

  useEffect(() => {
    const newLeadData = {
      ...leadData,
      leadData: {
        group: {
          ...leadData.group,
        },
        dealValue: 0,
        clubId: '',
        contractFlowToken: '',
        seats: 1,
      },
    };

    async function load() {
      loadingGroupDataRef.current = true;

      const { data, error } = await ContractFlowClient().getGroupInfo(clubId);

      if (data) {
        newLeadData.leadData.group = data;
      }

      if (dealValue && clubId) {
        newLeadData.leadData.dealValue = dealValue;
        newLeadData.leadData.clubId = clubId;
      }

      if (cfUuid) {
        newLeadData.leadData.contractFlowToken = cfUuid;
      }

      newLeadData.leadData.seats = seats;

      dispatch(setLeadData(newLeadData));

      if (error) {
        if (error?.response?.status === 404) {
          window.location.href = UNKNOWN_CLUB_REDIRECT;
        } else {
          handleErrorMessage(error);
        }
      }
      setIsContractFlowLoading(false);
    }

    if (clubId && !loadingGroupDataRef.current) {
      load();
    }
  }, [clubId, loadingGroupDataRef.current, cfUuid, dealValue]);

  useEffect(() => {
    if (cfUuid) {
      setIsContractFlowLoading(true);

      if (currentStep > 0) {
        setIsContractFlowLoading(false);
      }
    }
  }, [isContractFlowLoading, currentStep, cfUuid]);

  /* Rendered on the flow's own dark screen rather than the light General layout.
     A reload lands here first, and the light version came with the marketing
     header — nav links and "Join now" — so the member watched a different-looking
     site load before step 0 replaced it. */
  const loading = (
    <FlowScreen>
      <S.LoadingWrapper>
        <LoadingState>
          <S.LoadingContent>
            Just a moment.
            <br />
            We're loading the information for you.
          </S.LoadingContent>
        </LoadingState>
      </S.LoadingWrapper>
    </FlowScreen>
  );

  if (leadData.group.id) {
    /* No General wrapper: every step renders its own FlowScreen, so wrapping them
       would put the marketing layout behind a full-bleed overlay for nothing. */
    return isContractFlowLoading ? loading : <StepsContent />;
  }

  return loading;
};

export default Flow;
